import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { updateOffset } from 'store/appStateSlice'
import { getCurrentOffset } from 'store/appStateSlice/selectors'
import { ReactComponent as ChevronIcon } from 'assets/icons/IconChevronLeft.svg'
import { ReactComponent as TriangleIcon } from 'assets/icons/IconTriangle.svg'
import { ReactComponent as PencilIcon } from 'assets/icons/IconPencil.svg'
import { TPaginator } from 'types/paginator'
import { TAccountWithRoles } from 'types/accounts'
import { TPosAccount } from 'types/posAccounts'
import { TRole } from 'types/roles'
import { TLocation, RootState } from 'types/store'
import useGetPathname from 'hooks/useGetPathname'

type TProps = {
  paginator: TPaginator<TAccountWithRoles | TRole | TPosAccount>
  handlePersistPaginationPage: (val: number) => void
  currentPersistedPaginationPage: number
  selectedItemsBulkAction?: () => void
  selectedItemsBulkAmount?: number
}

const Pagination = ({
  currentPersistedPaginationPage,
  handlePersistPaginationPage,
  paginator,
  selectedItemsBulkAction,
  selectedItemsBulkAmount,
}: TProps) => {
  const { start, end, count, previousPage, nextPage, page, goToPage } = paginator

  const isNextPageNotExists = count === end
  const isPrevPageNotExist = start === 1
  const showBulkOptions = selectedItemsBulkAmount && selectedItemsBulkAmount > 0

  const dispatch = useDispatch()
  const { pathnameWithoutSlash } = useGetPathname()

  const offSet = useSelector((state) =>
    getCurrentOffset(state as RootState, pathnameWithoutSlash as TLocation)
  )

  useEffect(() => {
    goToPage(currentPersistedPaginationPage, Number(offSet))
  }, [currentPersistedPaginationPage])

  const handleOnChange = () => (event: React.ChangeEvent<HTMLSelectElement>) => {
    goToPage(0, Number(event.target.value))
    handlePersistPaginationPage(0)
    dispatch(
      updateOffset({
        location: pathnameWithoutSlash as TLocation,
        offSet: parseInt(event.target.value),
      })
    )
  }

  const handleNextPage = () => {
    handlePersistPaginationPage(page + 1)
    nextPage()
  }

  const handlePreviousPage = () => {
    handlePersistPaginationPage(page - 1)
    previousPage()
  }

  return (
    <>
      {showBulkOptions && (
        <div className="bulk">
          <span className="selected-items-amount">{selectedItemsBulkAmount}</span>
          <button className="selected-items-bulk-action" onClick={selectedItemsBulkAction}>
            Bulk Edit <PencilIcon />
          </button>
        </div>
      )}
      <div className="pagination__container">
        <div className="offset-controller">
          <div className="pagination__select-wrapper">
            <label htmlFor="offset" className="pagination__title">
              Rows per page:
            </label>
            <select
              className="pagination__select"
              name="offset"
              id="offset"
              value={Number(offSet)}
              onChange={handleOnChange()}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <TriangleIcon className="dropdown-pagination-icon" />
          </div>
        </div>
        <div className="pagination">
          <span className="pagination__info">
            {start} - {end} of {count}
          </span>
          <button
            className="pagination__previous"
            disabled={isPrevPageNotExist}
            onClick={handlePreviousPage}
          >
            <ChevronIcon />
          </button>
          <button
            className="pagination__next"
            disabled={isNextPageNotExists}
            onClick={handleNextPage}
          >
            <ChevronIcon />
          </button>
        </div>
      </div>
    </>
  )
}

export default Pagination
